// src/pages/Accounting/Reconcile/index.tsx

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PALETTE } from "./constants";
import { mockAccounts, mockPriorReconciliations } from "./mockData";
import {
  formatIsoToUserDate,
  getLastReconciliation,
  parseUserDate,
  parseUserMoney,
  formatDateLong,
} from "./utils";
import type { SetupFormState, ReconciliationDraft } from "./types";
import { SetupForm } from "./components/SetupForm";
import { VelaIntroBlock } from "./components/VelaIntroBlock";
import { RefresherLink } from "./components/RefresherLink";
import { LastReconciledLine } from "./components/LastReconciledLine";
import { ResumeCard } from "./components/ResumeCard";
import { saveDraft, clearDraft, loadAnyDraft } from "./storage";

const TOP_ACTIONS: { label: string; to: string }[] = [
  { label: "Chart of accounts", to: "/accounting/chart-of-accounts" },
  { label: "Register", to: "/accounting/register" },
  { label: "Summary", to: "/accounting/reconcile/summary" },
  { label: "History by account", to: "/accounting/reconcile/history" },
];

export function ReconcileHeader() {
  const navigate = useNavigate();
  return (
    <div style={headerStyles.wrap}>
      <div style={headerStyles.crumb}>
        <span style={headerStyles.crumbLink} onClick={() => navigate("/accounting")}>
          Accounting
        </span>
        <span style={headerStyles.crumbSep}>/</span>
        <span>Reconcile</span>
      </div>
      <div style={headerStyles.titleRow}>
        <div style={{ minWidth: 0 }}>
          <h1 style={headerStyles.title}>Reconcile</h1>
          <div style={headerStyles.sub}>
            Match what Novala thinks happened to what the bank actually recorded.
          </div>
        </div>
        <div style={headerStyles.actions}>
          {TOP_ACTIONS.map((a) => (
            <button
              key={a.label}
              type="button"
              onClick={() => navigate(a.to)}
              style={headerStyles.actionBtn}
            >
              {a.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

const headerStyles: Record<string, React.CSSProperties> = {
  wrap: { padding: "14px 32px 14px" },
  crumb: {
    fontSize: 13,
    color: PALETTE.ink3,
    marginBottom: 4,
    fontWeight: 500,
  },
  crumbLink: { color: PALETTE.ink3, cursor: "pointer" },
  crumbSep: { color: PALETTE.ink3, margin: "0 6px" },
  titleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 24,
  },
  title: {
    fontSize: 32,
    fontWeight: 800,
    margin: 0,
    letterSpacing: "-0.02em",
    color: PALETTE.ink,
  },
  sub: {
    fontSize: 13.5,
    color: PALETTE.ink3,
    marginTop: 6,
    fontWeight: 500,
  },
  actions: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    flexShrink: 0,
  },
  actionBtn: {
    background: "#fff",
    border: `1px solid ${PALETTE.line}`,
    color: PALETTE.ink,
    fontWeight: 600,
    fontSize: 13,
    padding: "8px 14px",
    borderRadius: 10,
    cursor: "pointer",
    fontFamily: "inherit",
  },
};

export default function ReconcilePage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const defaultAccountId = mockAccounts[0]?.id ?? "";
  const [state, setState] = useState<SetupFormState>({
    accountId: defaultAccountId,
    statementEndingDate: "",
    statementEndingBalance: "",
  });
  const [existingDraft, setExistingDraft] = useState<ReconciliationDraft | null>(null);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [savedToast, setSavedToast] = useState(false);
  const [lockedToast, setLockedToast] = useState<{ periodLabel: string } | null>(null);

  useEffect(() => {
    const d = loadAnyDraft();
    if (d) setExistingDraft(d);
  }, []);

  // Pick up the ?reconciled=1&accountId=…&periodEnd=… trio emitted by the
  // Session page after a successful Sign and lock. Show the toast, dismiss
  // it after 5s, and clean the URL so a refresh does not re-trigger it.
  useEffect(() => {
    if (params.get("reconciled") !== "1") return;
    const periodEnd = params.get("periodEnd");
    const periodLabel = periodEnd ? formatDateLong(periodEnd) : "the period";
    setLockedToast({ periodLabel });
    const t = window.setTimeout(() => setLockedToast(null), 5000);

    const next = new URLSearchParams(params);
    next.delete("reconciled");
    next.delete("accountId");
    next.delete("periodEnd");
    setParams(next, { replace: true });

    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lastRec = useMemo(
    () => getLastReconciliation(state.accountId, mockPriorReconciliations),
    [state.accountId],
  );

  const draftAccount = useMemo(
    () => (existingDraft ? mockAccounts.find((a) => a.id === existingDraft.accountId) : undefined),
    [existingDraft],
  );

  const handleResumeDraft = () => {
    if (!existingDraft) return;
    const draft = existingDraft;
    const next: SetupFormState = {
      accountId: draft.accountId,
      statementEndingDate: formatIsoToUserDate(draft.statementEndingDate),
      statementEndingBalance: formatMoneyForInput(draft.statementEndingBalance),
    };
    setState(next);
    setDraftLoaded(true);
    setExistingDraft(null);
    handleStartReconciling(next);
  };

  const handleDiscardDraft = () => {
    if (!existingDraft) return;
    clearDraft(existingDraft.accountId);
    setExistingDraft(null);
  };

  const handleStartReconciling = (next: SetupFormState) => {
    const dateIso = parseUserDate(next.statementEndingDate);
    const money = parseUserMoney(next.statementEndingBalance);
    if (!dateIso || money === null) return;
    const beginningBalance = lastRec?.statementEndingBalance ?? 0;
    const periodStart = lastRec ? addOneDay(lastRec.periodEnd) : firstOfYear(dateIso);
    const qp = new URLSearchParams({
      accountId: next.accountId,
      periodStart,
      periodEnd: dateIso,
      beginningBalance: String(beginningBalance),
      statementEndingBalance: String(money),
    });
    clearDraft(next.accountId);
    navigate(`/accounting/reconcile/session?${qp.toString()}`);
  };

  const handleSaveForLater = (next: SetupFormState) => {
    const dateIso = parseUserDate(next.statementEndingDate);
    const money = parseUserMoney(next.statementEndingBalance);
    if (!next.accountId) return;
    saveDraft({
      accountId: next.accountId,
      statementEndingDate: dateIso ?? next.statementEndingDate,
      statementEndingBalance: money ?? 0,
      savedAt: new Date().toISOString(),
    });
    setSavedToast(true);
    setTimeout(() => setSavedToast(false), 2400);
  };

  const handleViewHistory = () => {
    navigate("/accounting/reconcile/history");
  };

  const showResumeCard = existingDraft && !draftLoaded;

  return (
    <div style={pageStyle}>
      {showResumeCard ? (
        <ResumeCard
          draft={existingDraft}
          account={draftAccount}
          onResume={handleResumeDraft}
          onDiscard={handleDiscardDraft}
        />
      ) : (
        <>
          {lastRec ? (
            <>
              <RefresherLink />
              <LastReconciledLine
                reconciliation={lastRec}
                onViewHistory={handleViewHistory}
              />
            </>
          ) : (
            <VelaIntroBlock />
          )}

          <SetupForm
            accounts={mockAccounts}
            state={state}
            onStateChange={setState}
            lastReconciliation={lastRec}
            onStartReconciling={handleStartReconciling}
            onSaveForLater={handleSaveForLater}
            fromDraft={false}
          />
        </>
      )}

      {savedToast && (
        <div style={toastStyle}>
          Draft saved. You can pick it up later from this page.
        </div>
      )}

      {lockedToast && (
        <div style={lockedToastStyle}>
          <span style={lockedCheckStyle}>&check;</span>
          Reconciliation locked for {lockedToast.periodLabel}.
          <a
            style={lockedLinkStyle}
            onClick={() => navigate("/accounting/reconcile/history")}
          >
            View in History
          </a>
        </div>
      )}
    </div>
  );
}

function formatMoneyForInput(amount: number): string {
  return `$${amount.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function addOneDay(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

function firstOfYear(iso: string): string {
  const [yyyy] = iso.split("-");
  return `${yyyy}-01-01`;
}

const pageStyle: React.CSSProperties = {
  width: "100%",
  margin: 0,
  padding: "28px 32px 90px",
};
const toastStyle: React.CSSProperties = {
  position: "fixed",
  bottom: 24,
  left: "50%",
  transform: "translateX(-50%)",
  background: PALETTE.ink,
  color: "#fff",
  padding: "10px 18px",
  borderRadius: 10,
  fontSize: 13,
  fontWeight: 500,
  boxShadow: "0 10px 32px rgba(0,0,0,0.2)",
  zIndex: 100,
};
const lockedToastStyle: React.CSSProperties = {
  position: "fixed",
  bottom: 24,
  left: "50%",
  transform: "translateX(-50%)",
  background: PALETTE.ink,
  color: "#fff",
  padding: "12px 20px",
  borderRadius: 10,
  fontSize: 13.5,
  fontWeight: 500,
  boxShadow: "0 10px 32px rgba(0,0,0,0.2)",
  zIndex: 100,
  display: "inline-flex",
  alignItems: "center",
  gap: 10,
};
const lockedCheckStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 22,
  height: 22,
  borderRadius: "50%",
  background: "#065F46",
  color: "#fff",
  fontSize: 13,
  fontWeight: 700,
  lineHeight: 1,
};
const lockedLinkStyle: React.CSSProperties = {
  color: "#86EFAC",
  fontWeight: 600,
  textDecoration: "underline",
  cursor: "pointer",
  marginLeft: 6,
};